import React from "react";
import { defineMessages } from "react-intl";

import {
	mandatoryLabProcedures,
	mandatoryViralSerologyQueries,
	queries
} from "lib/build/dashboards/ktx/queries";

import { useEventsByQuery } from "src/features/events";
import { BmiTrendChart } from "src/features/stats";
import { useSectionList } from "src/modules/ktx/dashboard/useSectionList";
import {
	filterSectionList,
	Search,
	SectionList
} from "src/modules/shared/dashboard";
import { compareByTimestampReverse } from "src/modules/shared/dashboard/utils/sort";
import { MeasurementSummary } from "src/modules/shared/measurement-summary";

const messages = defineMessages({
	viralSerology: {
		id: "ktx.dashboard.viral_serology",
		defaultMessage: "Vírusszerológia"
	},
	labProcedures: {
		id: "ktx.dashboard.lab_procedures",
		defaultMessage: "Laborvizsgálatok"
	}
});

const mandatoryQueries = [
	{
		label: messages.viralSerology,
		form: "ktx-viral-serology",
		queries: Object.values(mandatoryViralSerologyQueries)
	},
	{
		label: messages.labProcedures,
		form: "ktx-lab-procedures",
		queries: Object.values(mandatoryLabProcedures)
	},
	queries.abdominalPelvisUltrasound,
	queries.accessStatement,
	queries.chestXRay,
	queries.dentalScreening,
	queries.earNoseThroat,
	queries.gynecology,
	queries.medicalTreatment,
	queries.nativeAbdominalXRay,
	queries.ophthalmology,
	queries.otherDisorder,
	queries.pregnanciesAndChildrenStatement,
	queries.previousOtherOrganTransplantationStatement,
	queries.renalReplacementTherapyStatement,
	queries.urology,
	queries.weight,
	queries.kidneyDiseaseEthiology,
	queries.signedStatementOfConsentFyrms,
	queries.signedStatementOfConsentOvsz,
	queries.bloodPressure,
	queries.oncologyDiseases,

	queries.cardiovascularDiseases,
	queries.metabolicDiseases,
	queries.hepatologyDisease,
	queries.renalAnaemiaHematologyStatement,
	queries.haemostaseology,
	queries.nephrectomyGraftectomy,
	queries.otherSurgicalIntervention,
	queries.otherSurgicalIntervention,
	queries.disease,
	queries.vaccination,
	queries.alcohol,
	queries.smoking,
	queries.allergiesAndSensitivities,
	queries.cardiologyEkg,
	queries.cardiologyTranstorachalisEcho,

	queries.bloodGroup,
	queries.graftlossStatement
];

export const Dashboard: React.FunctionComponent = () => {
	const [query, setQuery] = React.useState("");
	const sections = useSectionList();
	const filteredSections = React.useMemo(
		() => filterSectionList(sections, query),
		[sections, query]
	);
	return (
		<>
			<Search query={query} onChangeQuery={setQuery} />
			<MeasurementSummary groupsOrQueries={mandatoryQueries} />
			<BmiTrendChartRenderer />
			<SectionList sections={filteredSections} />
		</>
	);
};

const BmiTrendChartRenderer: React.FunctionComponent = () => {
	const unsortedEvents = useEventsByQuery<number>(queries.bmi);
	const sortedEvents = React.useMemo(() => {
		const result = unsortedEvents.slice();
		result.sort(compareByTimestampReverse);
		return result;
	}, [unsortedEvents]);
	return <BmiTrendChart events={sortedEvents} />;
};
